import React from "react";
import { Lock, Sparkles, ArrowUpRight } from "lucide-react";

const PLAN_DETAILS = {
  basic: { name: "Basic", price: "$19 USD/usuario/mes", color: "blue" },
  standard: { name: "Standard", price: "$39 USD/usuario/mes", color: "purple" },
  pro: { name: "Pro", price: "$59 USD/usuario/mes", color: "amber" },
  enterprise: { name: "Enterprise", price: "Contactar ventas", color: "emerald" },
};

const FEATURE_NAMES = {
  reportes_xml_sat_uif: "Reportes XML SAT/UIF",
  dashboard_cumplimiento: "Dashboard de Cumplimiento",
  ocr_automatico: "OCR Automático",
  bitacora_audit_log: "Bitácora / Audit Log",
  api_access: "Acceso API",
  white_label: "White Label",
};

/**
 * UpsellBanner - Shows when user tries to access a feature not in their plan
 */
export function UpsellBanner({ feature, requiredPlan, onClose }) {
  const planInfo = PLAN_DETAILS[requiredPlan] || PLAN_DETAILS.enterprise;
  const featureName = FEATURE_NAMES[feature] || feature;

  return (
    <div className="bg-gradient-to-r from-slate-800/80 to-slate-900/80 border border-slate-700 rounded-xl p-6 mb-6">
      <div className="flex items-start gap-4">
        <div className="p-3 bg-amber-500/10 rounded-lg">
          <Lock className="w-6 h-6 text-amber-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-400" />
            Función Premium
          </h3>
          <p className="text-slate-300 mt-1">
            <span className="font-medium text-white">{featureName}</span> está disponible 
            en el plan <span className="font-semibold text-amber-400">{planInfo.name}</span>.
          </p>
          <p className="text-slate-400 text-sm mt-2">
            Actualiza tu plan para desbloquear esta función y muchas más.
          </p>
          <div className="mt-4 flex items-center gap-3">
            <button className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg transition-colors">
              Ver planes
              <ArrowUpRight className="w-4 h-4" />
            </button>
            {onClose && (
              <button 
                onClick={onClose}
                className="px-4 py-2 text-slate-400 hover:text-slate-200 text-sm"
              >
                Cerrar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * UpsellModal - Full-screen modal for upsell
 */
export function UpsellModal({ feature, requiredPlan, onClose }) {
  const planInfo = PLAN_DETAILS[requiredPlan] || PLAN_DETAILS.enterprise;
  const featureName = FEATURE_NAMES[feature] || feature;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <div className="text-center">
          <div className="inline-flex p-4 bg-amber-500/10 rounded-full mb-4">
            <Lock className="w-8 h-8 text-amber-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">
            Función no disponible
          </h2>
          <p className="text-slate-300 mb-4">
            <span className="font-medium text-white">{featureName}</span> requiere 
            el plan <span className="font-semibold text-amber-400">{planInfo.name}</span>.
          </p>
          <p className="text-slate-400 text-sm mb-6">
            {planInfo.price}
          </p>
          <div className="space-y-3">
            <button className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-lg transition-colors flex items-center justify-center gap-2">
              <Sparkles className="w-5 h-5" />
              Actualizar a {planInfo.name}
            </button>
            <button 
              onClick={onClose}
              className="w-full py-3 text-slate-400 hover:text-slate-200 text-sm"
            >
              Volver
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * LimitReachedBanner - Shows when a monthly limit is reached
 */
export function LimitReachedBanner({ limitType, current, max, resetDate }) {
  const limitNames = {
    onboardings: "onboardings",
    screenings: "screenings",
    pdfs: "generación de PDFs",
    ocr: "procesamiento OCR",
    clients: "clientes",
  };

  const limitName = limitNames[limitType] || limitType;
  const formattedDate = resetDate ? new Date(resetDate).toLocaleDateString("es-MX", {
    day: "numeric",
    month: "long",
  }) : "el próximo mes";

  return (
    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-red-500/20 rounded-lg">
          <Lock className="w-5 h-5 text-red-400" />
        </div>
        <div>
          <h3 className="font-semibold text-red-400">
            Límite alcanzado
          </h3>
          <p className="text-slate-300 text-sm mt-1">
            Has alcanzado el límite de <span className="font-medium">{limitName}</span> para 
            este mes ({current}/{max}).
          </p>
          <p className="text-slate-400 text-sm mt-1">
            Tu límite se renueva {formattedDate}.
          </p>
          <button className="mt-3 text-sm text-amber-400 hover:text-amber-300 font-medium">
            Actualizar plan para más límites →
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * FeatureGate - Wrapper that shows upsell if feature is not available
 */
export function FeatureGate({ feature, requiredPlan, children, showBanner = true }) {
  const { hasFeature } = require("../context/OrganizationContext").useOrganization();
  
  if (hasFeature(feature)) {
    return children;
  }
  
  if (showBanner) {
    return <UpsellBanner feature={feature} requiredPlan={requiredPlan} />;
  }
  
  return null;
}

export default UpsellBanner;
